import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleMap from '../components/GoogleMaps';
import "../styles/createpackage.css";
//  import "../styles/CreatePackages.css";
import "../styles/map.css";

// Constants for better maintainability
const API_ENDPOINTS = {
  REVERSE_GEOCODE: '/api/maps/reverse-geocode',
  CREATE_PACKAGE: '/api/packages'
};

const VALIDATION = {
  MAX_WORDS: 15,
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  MAX_PEOPLE: 20,
  MIN_PEOPLE: 1,
  MAX_POINTS_PER_LOCATION: 5
};

const DISTRICTS = [
  'Kohima',
  'Dimapur', 
  'Mon',
  'Peren'
];

const PACKAGE_CATEGORIES = [
  'Select Package Category',
  'Adventure',
  'Relaxation',
  'Cultural',
  'Business',
  'Festival',
  'Trip'
];

const CreatePackage = () => {
  const [formData, setFormData] = useState({
    packageName: '',
    shortDescription: '',
    primaryLocation: '',
    numPeople: '',
    packageCategory: PACKAGE_CATEGORIES[0],
    locationCoords: null,
    pickupAvailable: 'not available',
    dropoffAvailable: 'not available',
  });
  
  const [pickupLocations, setPickupLocations] = useState([]);
  const [dropoffLocations, setDropoffLocations] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [mapSearch, setMapSearch] = useState('');
  
  // Location management states
  const [tempLocation, setTempLocation] = useState({ district: 'Select District', points: [] });
  const [showLocationMap, setShowLocationMap] = useState(false);
  const [currentLocationType, setCurrentLocationType] = useState('');
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [selectedPointCoords, setSelectedPointCoords] = useState(null);

  const navigate = useNavigate();

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Utility functions
  const capitalizeWords = useCallback((str) => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }, []);

  const capitalizeFirstLetter = useCallback((str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  // Primary location map handler
  const handleMapLocationSelect = useCallback(async (coords) => {
    setSelectedCoords(coords);
    
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REVERSE_GEOCODE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ lat: coords.lat, lng: coords.lng })
      });

      const result = await response.json();
      
      if (result.success && result.results.length > 0) {
        const address = result.results[0].formatted_address;
        setFormData(prev => ({
          ...prev,
          primaryLocation: address,
          locationCoords: coords
        }));
        setSuccess(`Location selected: ${address}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setFormData(prev => ({
          ...prev,
          primaryLocation: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
          locationCoords: coords
        }));
      }
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        primaryLocation: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
        locationCoords: coords
      }));
    }
  }, [API_BASE_URL]);

  // Location map selection handler
  const handleLocationMapSelect = useCallback((coords) => {
    setSelectedPointCoords(coords);
    setSuccess('Location selected on map. Click "Add This Point" to confirm.');
    setTimeout(() => setSuccess(null), 3000);
  }, []);

  // Add selected point
  const addSelectedPoint = useCallback(async () => {
    if (!selectedPointCoords) {
      setError('Please select a location on the map first');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REVERSE_GEOCODE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ lat: selectedPointCoords.lat, lng: selectedPointCoords.lng })
      });

      const result = await response.json();
      
      let address;
      if (result.success && result.results.length > 0) {
        address = result.results[0].formatted_address;
      } else {
        address = `Lat: ${selectedPointCoords.lat.toFixed(6)}, Lng: ${selectedPointCoords.lng.toFixed(6)}`;
      }
      
      const newPoint = {
        location: address,
        coordinates: selectedPointCoords,
        price: ''
      };
      
      if (tempLocation.points.length < VALIDATION.MAX_POINTS_PER_LOCATION) {
        setTempLocation(prev => ({
          ...prev,
          points: [...prev.points, newPoint]
        }));
        setSelectedPointCoords(null);
        setSuccess(`Point added: ${address}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(`Maximum ${VALIDATION.MAX_POINTS_PER_LOCATION} points per location reached`);
      }
      
    } catch (error) {
      const fallbackAddress = `Lat: ${selectedPointCoords.lat.toFixed(6)}, Lng: ${selectedPointCoords.lng.toFixed(6)}`;
      const newPoint = {
        location: fallbackAddress,
        coordinates: selectedPointCoords,
        price: ''
      };
      
      if (tempLocation.points.length < VALIDATION.MAX_POINTS_PER_LOCATION) {
        setTempLocation(prev => ({
          ...prev,
          points: [...prev.points, newPoint]
        }));
        setSelectedPointCoords(null);
      } else {
        setError(`Maximum ${VALIDATION.MAX_POINTS_PER_LOCATION} points per location reached`);
      }
    }
  }, [selectedPointCoords, tempLocation.points, API_BASE_URL]);

  // Location management functions
  const startAddingLocation = useCallback((type) => {
    setCurrentLocationType(type);
    setTempLocation({ district: 'Select District', points: [] });
    setShowLocationEditor(true);
    setSelectedPointCoords(null);
  }, []);

  const startEditingLocation = useCallback((type, index) => {
    const locations = type === 'pickup' ? pickupLocations : dropoffLocations;
    setCurrentLocationType(type);
    setTempLocation({ ...locations[index], editingIndex: index });
    setShowLocationEditor(true);
    setSelectedPointCoords(null);
  }, [pickupLocations, dropoffLocations]);

  const saveLocation = useCallback(() => {
    if (tempLocation.district === 'Select District') {
      setError('Please select a district');
      return;
    }

    if (tempLocation.points.length === 0) {
      setError('Please add at least one point');
      return;
    }

    // Validate all prices
    for (let i = 0; i < tempLocation.points.length; i++) {
      const point = tempLocation.points[i];
      if (!point.price || isNaN(point.price) || parseFloat(point.price) < 0) {
        setError(`Please enter a valid price for point ${i + 1}`);
        return;
      }
    }

    const locationWithPrices = {
      district: tempLocation.district,
      points: tempLocation.points.map(point => ({
        location: point.location,
        coordinates: point.coordinates,
        price: parseFloat(point.price)
      }))
    };

    if (currentLocationType === 'pickup') {
      if (tempLocation.editingIndex !== undefined) {
        setPickupLocations(prev => 
          prev.map((loc, index) => 
            index === tempLocation.editingIndex ? { ...locationWithPrices, id: loc.id } : loc
          )
        );
      } else {
        setPickupLocations(prev => [...prev, { ...locationWithPrices, id: Date.now() }]);
      }
    } else {
      if (tempLocation.editingIndex !== undefined) {
        setDropoffLocations(prev => 
          prev.map((loc, index) => 
            index === tempLocation.editingIndex ? { ...locationWithPrices, id: loc.id } : loc
          )
        );
      } else {
        setDropoffLocations(prev => [...prev, { ...locationWithPrices, id: Date.now() }]);
      }
    }

    cancelEditing();
    setSuccess(tempLocation.editingIndex !== undefined ? 'Location updated successfully' : 'Location added successfully');
    setTimeout(() => setSuccess(null), 3000);
  }, [tempLocation, currentLocationType]);

  const removeLocation = useCallback((type, index) => {
    if (type === 'pickup') {
      setPickupLocations(prev => prev.filter((_, i) => i !== index));
    } else {
      setDropoffLocations(prev => prev.filter((_, i) => i !== index));
    }
    setSuccess('Location removed');
    setTimeout(() => setSuccess(null), 3000);
  }, []);

  const removePoint = useCallback((pointIndex) => {
    setTempLocation(prev => ({
      ...prev,
      points: prev.points.filter((_, index) => index !== pointIndex)
    }));
  }, []);

  const updatePointPrice = useCallback((pointIndex, price) => {
    setTempLocation(prev => ({
      ...prev,
      points: prev.points.map((point, index) => 
        index === pointIndex ? { ...point, price } : point
      )
    }));
  }, []);

  const cancelEditing = useCallback(() => {
    setTempLocation({ district: 'Select District', points: [] });
    setCurrentLocationType('');
    setShowLocationEditor(false);
    setSelectedPointCoords(null);
  }, []);

  const startAddingPoint = useCallback(() => {
    if (tempLocation.district === 'Select District') {
      setError('Please select a district first');
      return;
    }
    setMapSearch(tempLocation.district);
    setSelectedPointCoords(null);
    setShowLocationMap(true);
  }, [tempLocation.district]);

  const closeLocationMap = useCallback(() => {
    setShowLocationMap(false);
    setSelectedPointCoords(null);
  }, []);

  // Input handlers
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    if (name === 'packageName') {
      formattedValue = capitalizeWords(value);
    } else if (name === 'shortDescription') {
      formattedValue = capitalizeFirstLetter(value);
    } else if (name === 'primaryLocation') {
      formattedValue = capitalizeWords(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  }, [capitalizeWords, capitalizeFirstLetter]);

  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    const words = value.trim().split(/\s+/).filter(word => word.length > 0);
    
    if (words.length <= VALIDATION.MAX_WORDS) {
      const formattedValue = capitalizeFirstLetter(value);
      setFormData(prev => ({
        ...prev,
        shortDescription: formattedValue
      }));
      setWordCount(words.length);
    }
  }, [capitalizeFirstLetter]);

  // File handling
  const handleFileUpload = useCallback((file) => {
    if (file) {
      if (file.size > VALIDATION.MAX_FILE_SIZE) {
        setError('File size must be less than 5MB');
        return;
      }
      
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setError('Only JPEG and PNG files are allowed');
        return;
      }
      
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  }, [handleFileUpload]);

  // Form validation
  const validateForm = useCallback(() => {
    if (!formData.packageName.trim()) {
      setError('Package name is required');
      return false;
    }

    if (!formData.shortDescription.trim()) {
      setError('Short description is required');
      return false;
    }

    if (wordCount === 0) {
      setError('Please enter a short description');
      return false;
    }

    if (!formData.primaryLocation.trim()) {
      setError('Primary location is required. Please select a location from the map.');
      return false;
    }

    if (!formData.numPeople || parseInt(formData.numPeople) < VALIDATION.MIN_PEOPLE) {
      setError(`Please enter a valid number of people (minimum ${VALIDATION.MIN_PEOPLE})`);
      return false;
    }

    if (parseInt(formData.numPeople) > VALIDATION.MAX_PEOPLE) {
      setError(`Number of people cannot exceed ${VALIDATION.MAX_PEOPLE}`);
      return false;
    }

    if (formData.packageCategory === PACKAGE_CATEGORIES[0]) {
      setError('Please select a package category');
      return false;
    }

    if (!logoFile) {
      setError('Please upload a logo/image');
      return false;
    }

    if (formData.pickupAvailable === 'available' && pickupLocations.length === 0) {
      setError('Please add at least one pickup location');
      return false;
    }

    if (formData.dropoffAvailable === 'available' && dropoffLocations.length === 0) {
      setError('Please add at least one dropoff location');
      return false;
    }

    return true;
  }, [formData, wordCount, logoFile, pickupLocations.length, dropoffLocations.length]);

  // Form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('packageName', formData.packageName.trim());
      submitData.append('shortDescription', formData.shortDescription.trim());
      submitData.append('primaryLocation', formData.primaryLocation.trim());
      submitData.append('numPeople', formData.numPeople);
      submitData.append('packageCategory', formData.packageCategory);
      submitData.append('pickupAvailable', formData.pickupAvailable);
      submitData.append('dropoffAvailable', formData.dropoffAvailable);
      
      if (formData.locationCoords) {
        submitData.append('locationCoords', JSON.stringify(formData.locationCoords));
      }
      
      if (pickupLocations.length > 0) {
        submitData.append('pickupLocations', JSON.stringify(pickupLocations));
      }
      
      if (dropoffLocations.length > 0) {
        submitData.append('dropoffLocations', JSON.stringify(dropoffLocations));
      }
      
      submitData.append('packageLogo', logoFile);

      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CREATE_PACKAGE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: submitData
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned an invalid response. Please try again.');
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to create package');
      }
      
      if (result.success) {
        setSuccess('Package created successfully! Redirecting...');
        setTimeout(() => {
          navigate(`/package-details?id=${result.packageCustomId}`);
        }, 1500);
      }
      
    } catch (error) {
      console.error('Package creation error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [formData, pickupLocations, dropoffLocations, logoFile, validateForm, navigate, API_BASE_URL]);

  return (
    <div className="create-package-container">
      <div className="container">
        <div className="back-arrow" onClick={() => navigate('/home')}>←</div>
        <h1>Create Package</h1>
        
        {success && (
          <div className="upload-error success">
            <span>✅</span>
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="upload-error error">
            <span>❌</span>
            <span>{error}</span>
          </div>
        )}

        <form className="create-form" onSubmit={handleSubmit}>
          {/* PACKAGE NAME */}
          <div className="form-group">
            <label htmlFor="package-name">Package Name:</label>
            <input 
              type="text" 
              id="package-name" 
              name="packageName"
              placeholder="Enter Package Name"
              value={formData.packageName}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />
            {!formData.packageName.trim() && (
              <small className="field-error">Package name is required</small>
            )}
          </div>

          {/* SHORT DESCRIPTION */}
          <div className="form-group">
            <label htmlFor="short-description">Short Description</label>
            <textarea
              id="short-description"
              name="shortDescription"
              rows="4"
              placeholder="Write something about the Package..."
              value={formData.shortDescription}
              onChange={handleDescriptionChange}
              required
              disabled={isLoading}
            />
            <div className="description-info">
              <small id="word-count">
                {wordCount}/{VALIDATION.MAX_WORDS} words
              </small>
              {!formData.shortDescription.trim() && (
                <small className="field-error">Short description is required</small>
              )}
            </div>
          </div>

          {/* PRIMARY LOCATION */}
          <div className="form-group">
            <label>Set Primary Location</label>
            
            <div className="location-search-container">
              <button 
                type="button" 
                className="select-location-btn"
                onClick={() => setShowMap(true)}
                disabled={isLoading}
              >
                🗺️ Select Location on Map
              </button>
            </div>

            {formData.primaryLocation && (
              <div className="selected-location">
                <strong>Selected Location:</strong> {formData.primaryLocation}
              </div>
            )}
            {!formData.primaryLocation.trim() && (
              <small className="field-error">Primary location is required</small>
            )}
          </div>

          {/* PRIMARY LOCATION MAP MODAL */}
          {showMap && (
            <div className="map-modal-overlay">
              <div className="map-modal">
                <div className="map-modal-header">
                  <h3>Select Location</h3>
                  <button className="close-map-btn" onClick={() => setShowMap(false)}>×</button>
                </div>
                
                <div className="map-search-container">
                  <input
                    type="text"
                    placeholder="Search for a location..."
                    className="map-search-input"
                    value={mapSearch}
                    onChange={(e) => setMapSearch(capitalizeWords(e.target.value))}
                  />
                </div>
                
                <div className="real-map-container">
                  <GoogleMap 
                    onLocationSelect={handleMapLocationSelect}
                    selectedCoords={selectedCoords}
                    searchLocation={mapSearch}
                  />
                </div>
                
                <div className="map-modal-actions">
                  <button 
                    onClick={() => setShowMap(false)}
                    className="confirm-location-btn"
                  >
                    Confirm Location
                  </button>
                  <button 
                    onClick={() => setShowMap(false)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PICKUP AVAILABILITY SECTION */}
          <div className="form-group">
            <label htmlFor="pickup-available">Pickup Service Available?</label>
            <select 
              id="pickup-available" 
              name="pickupAvailable"
              value={formData.pickupAvailable}
              onChange={handleInputChange}
              disabled={isLoading}
            >
              <option value="not available">Not Available</option>
              <option value="available">Available</option>
            </select>
          </div>

          {formData.pickupAvailable === 'available' && (
            <div className="locations-section">
              <div className="section-header">
                <h3>Pickup Locations</h3>
                
                <button 
                  type="button" 
                  className="add-location-btn"
                  onClick={() => startAddingLocation('pickup')}
                  disabled={isLoading}
                >
                  + Add New Location
                </button>
              </div>

              {showLocationEditor && currentLocationType === 'pickup' && (
                <div className="location-editor">
                  <div className="editor-header">
                    <h4>{tempLocation.editingIndex !== undefined ? 'Edit' : 'Add New'} Pickup Location</h4>
                  </div>
                  
                  <div className="district-selection">
                    <label>District:</label>
                    <select
                      value={tempLocation.district}
                      onChange={(e) => setTempLocation(prev => ({ ...prev, district: e.target.value }))}
                      className="district-select"
                      disabled={isLoading}
                    >
                      <option>Select District</option>
                      {DISTRICTS.map(district => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </select>
                    {tempLocation.district === 'Select District' && (
                      <small className="field-error">Please select a district</small>
                    )}
                  </div>

                  <div className="points-section">
                    <div className="points-header">
                      <h5>Points ({tempLocation.points.length}/{VALIDATION.MAX_POINTS_PER_LOCATION})</h5>
                      <button 
                        type="button" 
                        className="add-point-btn"
                        onClick={startAddingPoint}
                        disabled={tempLocation.district === 'Select District' || tempLocation.points.length >= VALIDATION.MAX_POINTS_PER_LOCATION || isLoading}
                      >
                        + Add Point
                      </button>
                    </div>

                    {tempLocation.points.length === 0 && (
                      <small className="field-error">Please add at least one point</small>
                    )}

                    {tempLocation.points.map((point, index) => (
                      <div key={index} className="point-item">
                        <div className="point-details">
                          <strong>Point {index + 1}:</strong> {point.location}
                        </div>
                        <div className="point-actions">
                          <input
                            type="number"
                            placeholder="Price"
                            min="0"
                            step="0.01"
                            value={point.price}
                            onChange={(e) => updatePointPrice(index, e.target.value)}
                            className="price-input"
                            disabled={isLoading}
                          />
                          {(!point.price || isNaN(point.price) || parseFloat(point.price) < 0) && (
                            <small className="field-error">Valid price required</small>
                          )}
                          <button
                            type="button"
                            className="remove-point-btn"
                            onClick={() => removePoint(index)}
                            disabled={isLoading}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="editor-actions">
                    <button 
                      type="button" 
                      className="save-location-btn"
                      onClick={saveLocation}
                      disabled={isLoading}
                    >
                      {tempLocation.editingIndex !== undefined ? 'Update Location' : 'Save Points'}
                    </button>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={cancelEditing}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {pickupLocations.length > 0 && (
                <div className="saved-locations">
                  <h4>Saved Pickup Locations ({pickupLocations.length})</h4>
                  {pickupLocations.map((location, index) => (
                    <div key={location.id} className="saved-location-item">
                      <div className="location-header">
                        <strong>{location.district}</strong>
                        <div className="location-actions">
                          <button
                            type="button"
                            className="edit-location-btn"
                            onClick={() => startEditingLocation('pickup', index)}
                            disabled={isLoading}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="remove-location-btn"
                            onClick={() => removeLocation('pickup', index)}
                            disabled={isLoading}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="location-points">
                        {location.points.map((point, pointIndex) => (
                          <div key={pointIndex} className="location-point">
                            <span className="point-address">{point.location}</span>
                            <span className="point-price">₹{point.price}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {formData.pickupAvailable === 'available' && pickupLocations.length === 0 && !showLocationEditor && (
                <small className="field-error">Please add at least one pickup location</small>
              )}
            </div>
          )}

          {/* DROPOFF AVAILABILITY SECTION */}
          <div className="form-group">
            <label htmlFor="dropoff-available">Dropoff Service Available?</label>
            <select 
              id="dropoff-available" 
              name="dropoffAvailable"
              value={formData.dropoffAvailable}
              onChange={handleInputChange}
              disabled={isLoading}
            >
              <option value="not available">Not Available</option>
              <option value="available">Available</option>
            </select>
          </div>

          {formData.dropoffAvailable === 'available' && (
            <div className="locations-section">
              <div className="section-header">
                <h3>Dropoff Locations</h3>
                
                <button 
                  type="button" 
                  className="add-location-btn"
                  onClick={() => startAddingLocation('dropoff')}
                  disabled={isLoading}
                >
                  + Add New Location
                </button>
              </div>

              {showLocationEditor && currentLocationType === 'dropoff' && (
                <div className="location-editor">
                  <div className="editor-header">
                    <h4>{tempLocation.editingIndex !== undefined ? 'Edit' : 'Add New'} Dropoff Location</h4>
                  </div>
                  
                  <div className="district-selection">
                    <label>District:</label>
                    <select
                      value={tempLocation.district}
                      onChange={(e) => setTempLocation(prev => ({ ...prev, district: e.target.value }))}
                      className="district-select"
                      disabled={isLoading}
                    >
                      <option>Select District</option>
                      {DISTRICTS.map(district => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </select>
                    {tempLocation.district === 'Select District' && (
                      <small className="field-error">Please select a district</small>
                    )}
                  </div>

                  <div className="points-section">
                    <div className="points-header">
                      <h5>Points ({tempLocation.points.length}/{VALIDATION.MAX_POINTS_PER_LOCATION})</h5>
                      <button 
                        type="button" 
                        className="add-point-btn"
                        onClick={startAddingPoint}
                        disabled={tempLocation.district === 'Select District' || tempLocation.points.length >= VALIDATION.MAX_POINTS_PER_LOCATION || isLoading}
                      >
                        + Add Point
                      </button>
                    </div>

                    {tempLocation.points.length === 0 && (
                      <small className="field-error">Please add at least one point</small>
                    )}

                    {tempLocation.points.map((point, index) => (
                      <div key={index} className="point-item">
                        <div className="point-details">
                          <strong>Point {index + 1}:</strong> {point.location}
                        </div>
                        <div className="point-actions">
                          <input
                            type="number"
                            placeholder="Price"
                            min="0"
                            step="0.01"
                            value={point.price}
                            onChange={(e) => updatePointPrice(index, e.target.value)}
                            className="price-input"
                            disabled={isLoading}
                          />
                          {(!point.price || isNaN(point.price) || parseFloat(point.price) < 0) && (
                            <small className="field-error">Valid price required</small>
                          )}
                          <button
                            type="button"
                            className="remove-point-btn"
                            onClick={() => removePoint(index)}
                            disabled={isLoading}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="editor-actions">
                    <button 
                      type="button" 
                      className="save-location-btn"
                      onClick={saveLocation}
                      disabled={isLoading}
                    >
                      {tempLocation.editingIndex !== undefined ? 'Update Location' : 'Save Location'}
                    </button>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={cancelEditing}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {dropoffLocations.length > 0 && (
                <div className="saved-locations">
                  <h4>Saved Dropoff Locations ({dropoffLocations.length})</h4>
                  {dropoffLocations.map((location, index) => (
                    <div key={location.id} className="saved-location-item">
                      <div className="location-header">
                        <strong>{location.district}</strong>
                        <div className="location-actions">
                          <button
                            type="button"
                            className="edit-location-btn"
                            onClick={() => startEditingLocation('dropoff', index)}
                            disabled={isLoading}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="remove-location-btn"
                            onClick={() => removeLocation('dropoff', index)}
                            disabled={isLoading}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="location-points">
                        {location.points.map((point, pointIndex) => (
                          <div key={pointIndex} className="location-point">
                            <span className="point-address">{point.location}</span>
                            <span className="point-price">₹{point.price}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {formData.dropoffAvailable === 'available' && dropoffLocations.length === 0 && !showLocationEditor && (
                <small className="field-error">Please add at least one dropoff location</small>
              )}
            </div>
          )}

          {/* NUMBER OF PEOPLE AND CATEGORY */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="number-people">Number of people</label>
              <input
                type="number"
                id="number-people"
                name="numPeople"
                placeholder="eg. 1,2,3,4,5,..."
                min="1"
                max="20"
                value={formData.numPeople}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {(!formData.numPeople || parseInt(formData.numPeople) < 1) && (
                <small className="field-error">Please enter a valid number (1-20)</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="package-category">Package Category:</label>
              <select 
                id="package-category" 
                name="packageCategory"
                value={formData.packageCategory}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              >
                {PACKAGE_CATEGORIES.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {formData.packageCategory === PACKAGE_CATEGORIES[0] && (
                <small className="field-error">Please select a category</small>
              )}
            </div>
          </div>

          {/* LOGO UPLOAD */}
          <div className="form-group">
            <label>Upload a Logo/Profile Picture</label>
            <div 
              className="upload-area" 
              onClick={() => !isLoading && document.getElementById('file-upload').click()}
            >
              {logoPreview ? (
                <div className="uploaded-image-container">
                  <img 
                    src={logoPreview} 
                    alt="Uploaded Logo" 
                    className="uploaded-image"
                    loading="lazy"
                  />
                </div>
              ) : (
                <>
                  <div className="upload-icon">🖼️</div>
                  <p>Click to upload image</p>
                  <p>Max 5MB • JPEG, PNG</p>
                </>
              )}
              <input 
                type="file" 
                id="file-upload" 
                accept="image/jpeg, image/png" 
                hidden 
                onChange={handleFileInputChange}
                disabled={isLoading}
              />
            </div>
            {!logoFile && (
              <small className="field-error">Please upload a logo/image</small>
            )}
          </div>

          <button 
            type="submit" 
            id="create-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'CREATE'}
          </button>
        </form>

        {/* PICKUP/DROPOFF LOCATION MAP MODAL */}
        {showLocationMap && (
          <div className="map-modal-overlay">
            <div className="map-modal">
              <div className="map-modal-header">
                <h3>Add Point for {tempLocation.district}</h3>
                <button className="close-map-btn" onClick={closeLocationMap}>×</button>
              </div>
              
              <div className="map-search-container">
                <input
                  type="text"
                  placeholder="Search for a location..."
                  className="map-search-input"
                  value={mapSearch}
                  onChange={(e) => setMapSearch(capitalizeWords(e.target.value))}
                />
              </div>
              
              <div className="real-map-container">
                <GoogleMap 
                  onLocationSelect={handleLocationMapSelect}
                  selectedCoords={selectedPointCoords}
                  searchLocation={mapSearch}
                />
              </div>
              
              <div className="map-modal-actions">
                <div className="points-info">
                  <div className="points-count">
                    Current Points: {tempLocation.points.length}/{VALIDATION.MAX_POINTS_PER_LOCATION}
                  </div>
                  {selectedPointCoords && (
                    <div className="selected-point-info">
                      Location selected - Ready to add
                    </div>
                  )}
                </div>
                <div className="map-action-buttons">
                  <button 
                    onClick={addSelectedPoint}
                    className="add-point-confirm-btn"
                    disabled={!selectedPointCoords}
                  >
                    Add This Point
                  </button>
                  <button 
                    onClick={closeLocationMap}
                    className="cancel-btn"
                  >
                    Confirm Points
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CreatePackage);